/**
 * EmptyState — canonical empty-state rendering for lists / data panels.
 *
 * Replaces the ad-hoc "<div>No results</div>" patterns scattered across the
 * codebase and provides a consistent shape: icon → title → description →
 * optional action.
 *
 * Used by /me/dashboard for the empty-profile case (previously hung on
 * skeleton forever — verified live), and by any other surface that needs to
 * communicate "there's nothing here yet" with an actionable next step.
 *
 * Usage:
 *
 *   <EmptyState
 *     icon={Inbox}
 *     title="No applications yet"
 *     description="Browse open positions to apply for your first job."
 *     action={<Button asChild><Link href="/jobs">Browse jobs</Link></Button>}
 *   />
 *
 * Architectural notes:
 *  - role="status" so assistive tech announces the empty state without
 *    interrupting the user.
 *  - Icon is aria-hidden — the title carries the meaning.
 *  - Uses the muted-foreground token so it sits visually below primary content
 *    but stays readable in both light and dark modes.
 */

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Visual size variant. `sm` for inline list-empty, `md` (default) for full panels. */
  size?: 'sm' | 'md';
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'py-8 px-4 gap-2' : 'py-16 px-6 gap-3',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-full bg-muted flex items-center justify-center',
          size === 'sm' ? 'w-10 h-10' : 'w-14 h-14',
        )}
      >
        <Icon
          aria-hidden="true"
          className={cn(
            'text-muted-foreground',
            size === 'sm' ? 'w-5 h-5' : 'w-7 h-7',
          )}
        />
      </div>
      <h3
        className={cn(
          'font-semibold text-foreground',
          size === 'sm' ? 'text-sm' : 'text-base',
        )}
      >
        {title}
      </h3>
      {description ? (
        <p
          className={cn(
            'text-muted-foreground max-w-md',
            size === 'sm' ? 'text-xs' : 'text-sm',
          )}
        >
          {description}
        </p>
      ) : null}
      {action ? <div className={size === 'sm' ? 'mt-1' : 'mt-2'}>{action}</div> : null}
    </div>
  );
}
